import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable, observable, runInAction } from 'mobx';

import type { TranslationMemoryEntryStore } from './TranslationMemoryEntryStore';
import type { AuthorRef } from './TranslationMemoryStore';
import type { TranslationMemoryImport } from '@src/repositories/TranslationMemoryImportRepository';
import type { Result } from '@src/services/common/Result';
import type {
  ColumnMapping,
  ImportPreview,
  ImportTarget,
} from '@src/services/translationMemoryImport/importPreview';
import type {
  CommitResult,
  TranslationMemoryImportService,
} from '@src/services/TranslationMemoryImportService';

const logger = LogManager.getLogger('stores:TranslationMemoryImport');

type ImportStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface TranslationMemoryImportsState {
  status: ImportStatus;
  imports: TranslationMemoryImport[];
  error: string | null;
}

interface Slice extends TranslationMemoryImportsState {
  inFlight: Promise<void> | null;
}

const emptySlice = (): Slice => ({
  status: 'idle',
  imports: [],
  error: null,
  inFlight: null,
});

/**
 * Per-showcase cache of retained document imports (PLAT-10848 / E11), powering
 * the import-history list. Mirrors `TranslationMemoryEntryStore`: an observable
 * showcase→slice map, load-once unless forced. `buildPreview` reads the entry
 * store's cached entries for the overwrite diff (DS4); `runImport` delegates to
 * the service, then prepends the new record here and applies the written entries
 * to the entry store in place (DS9) — no refetch (GraphQL lags CMA writes).
 */
export class TranslationMemoryImportStore {
  private slices = observable.map<string, Slice>(undefined, { deep: false });

  constructor(
    private service: TranslationMemoryImportService,
    private entryStore: TranslationMemoryEntryStore,
  ) {
    makeAutoObservable<this, 'service' | 'entryStore'>(this, {
      service: false,
      entryStore: false,
    });
  }

  getState(showcaseId: string): TranslationMemoryImportsState {
    const slice = this.slices.get(showcaseId) ?? emptySlice();
    return {
      status: slice.status,
      imports: slice.imports,
      error: slice.error,
    };
  }

  loadForShowcase(
    showcaseId: string,
    options: { force?: boolean } = {},
  ): Promise<void> {
    const existing = this.slices.get(showcaseId) ?? emptySlice();
    if (!options.force) {
      if (existing.status === 'ready') return Promise.resolve();
      if (existing.inFlight) return existing.inFlight;
    }

    const promise = this.service.fetchForShowcase(showcaseId).then(result => {
      runInAction(() => {
        const current = this.slices.get(showcaseId) ?? emptySlice();
        if (result.ok) {
          this.slices.set(showcaseId, {
            status: 'ready',
            imports: result.value,
            error: null,
            inFlight: null,
          });
        } else {
          logger.warn(
            'Failed to load translation memory imports',
            result.error,
          );
          this.slices.set(showcaseId, {
            status: 'error',
            imports: current.imports,
            error: "Couldn't load import history.",
            inFlight: null,
          });
        }
      });
    });

    this.slices.set(showcaseId, {
      ...existing,
      status: 'loading',
      error: null,
      inFlight: promise,
    });

    return promise;
  }

  /** Read a file's headers + suggested mapping for the configure step (DS5). */
  inspectFile(file: File) {
    return this.service.inspectFile(file);
  }

  /**
   * Detect the source/target column languages for the mismatch warning
   * (PLAT-10900). `signal` lets the caller abandon a superseded detect.
   */
  detectLanguages(file: File, mapping: ColumnMapping, signal?: AbortSignal) {
    return this.service.detectLanguages(file, mapping, signal);
  }

  /** Parse + validate + classify a file against the showcase's cached entries. */
  buildPreview(
    showcaseId: string,
    file: File,
    mapping: ColumnMapping,
    target: ImportTarget,
  ): Promise<Result<ImportPreview>> {
    const existingEntries = this.entryStore.getState(showcaseId).entries;
    return this.service.buildPreview(file, mapping, target, existingEntries);
  }

  /** Commit a previewed import, then apply results to both caches in place. */
  async runImport(args: {
    showcaseId: string;
    file: File;
    preview: ImportPreview;
    target: ImportTarget;
    author: AuthorRef | null;
    sourceLanguageRaw?: string | null;
    translationLanguageRaw?: string | null;
  }): Promise<Result<CommitResult>> {
    const result = await this.service.commit({
      showcaseId: args.showcaseId,
      file: args.file,
      preview: args.preview,
      target: args.target,
      personId: args.author?.id ?? null,
      sourceLanguageRaw: args.sourceLanguageRaw,
      translationLanguageRaw: args.translationLanguageRaw,
    });
    if (!result.ok) return result;

    const record: TranslationMemoryImport = args.author
      ? {
          ...result.value.import,
          createdByUser: { id: args.author.id, name: args.author.name },
        }
      : result.value.import;

    runInAction(() => {
      const current = this.slices.get(args.showcaseId) ?? emptySlice();
      this.slices.set(args.showcaseId, {
        ...current,
        status: 'ready',
        imports: [record, ...current.imports],
        inFlight: null,
      });
    });

    this.entryStore.applyImported(args.showcaseId, {
      created: result.value.created,
      overwritten: result.value.overwritten,
    });

    return result;
  }

  invalidate(showcaseId?: string): void {
    if (showcaseId) this.slices.delete(showcaseId);
    else this.slices.clear();
  }
}
